import { useCallback } from 'react';
import { Accordion, Card, EmptyState, PageHeader, Spinner, StatusPill } from '../components/ui';
import { Pagination } from '../components/Pagination';
import { usePagedQuery } from '../hooks/usePagedQuery';
import {
  listPlaybackErrors, type PlaybackErrorLog, type PlaybackErrorFilters,
} from '../services/endpoints/playback';
import { formatDateTime, relativeTime } from '../utils/format';
import './common.css';

const PLATFORMS = ['', 'ios', 'android', 'web'];
const STAGES = [
  '', 'shaka-error', 'segment-decrypt', 'load', 'no-player', 'unsupported',
  'no-key', 'shaka-missing', 'play', 'validate',
];

type Tone = 'moss' | 'ember' | 'rust' | 'brass';
const platformTone = (p?: string): Tone =>
  p === 'android' ? 'moss' : p === 'ios' ? 'brass' : p === 'web' ? 'ember' : 'rust';

export function PlaybackErrorsPage() {
  const fetcher = useCallback(
    ({ page, size, filters }: { page: number; size: number; filters: PlaybackErrorFilters }) =>
      listPlaybackErrors({ page, size, filters }),
    [],
  );
  const q = usePagedQuery<PlaybackErrorLog, PlaybackErrorFilters>({ fetcher, initialFilters: {}, pageSize: 25 });

  const setPlatform = (p: string) => q.setFilters({ ...q.filters, platform: p || undefined });
  const setStage = (s: string) => q.setFilters({ ...q.filters, stage: s || undefined });

  return (
    <div>
      <PageHeader
        eyebrow="17 — PLAYBACK ERRORS"
        title={<>When audio <em>won't play</em>.</>}
        lede="Client-reported playback failures. The classic 'transcript shows but audio is silent' case (especially on Android) usually means the binary segment path failed on the device. Filter by platform and the stage that failed."
      />

      <div className="nb-tabs" style={{ flexWrap: 'wrap', gap: 8 }}>
        {PLATFORMS.map((p) => (
          <button
            key={p || 'all'}
            onClick={() => setPlatform(p)}
            className={`nb-tabs__btn ${(q.filters.platform || '') === p ? 'is-active' : ''}`}
          >
            {p ? p.toUpperCase() : 'ALL PLATFORMS'}
          </button>
        ))}
      </div>
      <div className="nb-tabs" style={{ flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
        {STAGES.map((s) => (
          <button
            key={s || 'all'}
            onClick={() => setStage(s)}
            className={`nb-tabs__btn ${(q.filters.stage || '') === s ? 'is-active' : ''}`}
          >
            {s || 'ALL STAGES'}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 16 }}>
        {q.loading && !q.data.length ? (
          <Card><Spinner /> Loading…</Card>
        ) : q.data.length === 0 ? (
          <Card>
            <EmptyState
              title="No playback errors"
              hint="No failures logged for this filter. New client reports appear here automatically (logs auto-expire after 30 days)."
            />
          </Card>
        ) : (
          <>
            {q.data.map((log, i) => <ErrorRow key={log.id} log={log} defaultOpen={i === 0} />)}
            <Pagination page={q.page} pageSize={q.pageSize} total={q.totalCount} onPage={q.setPage} />
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <tr>
      <td className="nb-table__sub" style={{ width: 180, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{label}</td>
      <td className={mono ? 'nb-table__mono' : undefined} style={{ wordBreak: 'break-word' }}>{value}</td>
    </tr>
  );
}

const bool = (b?: boolean) => (b === undefined ? undefined : b ? 'yes' : 'no');

function ErrorRow({ log, defaultOpen }: { log: PlaybackErrorLog; defaultOpen: boolean }) {
  const mismatch = !!log.platform && !!log.serverPlatform && log.platform !== log.serverPlatform;
  const shaka = (log.shakaErrorCategory || log.shakaErrorCode)
    ? `${log.shakaErrorCategory ?? '?'}/${log.shakaErrorCode ?? '?'}`
    : null;
  const when = log.createdAt || log.serverTimestamp;

  return (
    <div style={{ marginBottom: 8 }}>
      <Accordion
        defaultOpen={defaultOpen}
        title={<><strong>{log.stage ?? 'error'}</strong>{' — '}{log.errorMessage || log.shakaErrorMessage || '—'}</>}
        subtitle={<>{when ? relativeTime(when) : ''} · {log.mediaId ?? 'no media'}</>}
        badge={
          <>
            <StatusPill tone={platformTone(log.platform)}>{log.platform ?? '?'}</StatusPill>
            {shaka && <StatusPill tone="rust">{shaka}</StatusPill>}
            {mismatch && <StatusPill tone="ember">platform mismatch</StatusPill>}
          </>
        }
      >
        <table className="nb-table">
          <tbody>
            <Row label="When (server)" value={formatDateTime(log.serverTimestamp || log.createdAt)} mono />
            <Row label="When (client)" value={log.clientTimestamp} mono />
            <Row label="Stage" value={log.stage} mono />
            <Row
              label="Platform"
              value={<>client <strong>{log.platform ?? '?'}</strong> · server <strong>{log.serverPlatform ?? '?'}</strong>{mismatch ? ' ⚠ mismatch' : ''}</>}
            />
            <Row label="User ID" value={log.userId} mono />
            <Row label="Media ID" value={log.mediaId} mono />
            <Row label="Class episode" value={bool(log.classEpisode)} />
            <Row label="Subscription active" value={bool(log.subscriptionActive)} />
            <Row label="Has IAP token" value={bool(log.hasOriginalTransactionId)} />
            <Row label="Has playback key" value={bool(log.hasPlaybackKey)} />
            <Row label="Shaka supported" value={bool(log.shakaSupported)} />
            <Row label="Stream format" value={log.format} mono />
            <Row label="Source URI" value={log.sourceUri} mono />
            <Row label="Shaka error" value={shaka} mono />
            <Row label="Shaka message" value={log.shakaErrorMessage} />
            <Row label="Shaka data" value={log.shakaErrorData} mono />
            <Row label="Error message" value={log.errorMessage} />
            <Row label="Device ID" value={log.deviceId} mono />
            <Row label="App" value={[log.appId, log.appVersion].filter(Boolean).join(' · ') || undefined} mono />
            <Row label="User agent" value={log.userAgent} mono />
            <Row label="Extra" value={log.extra ? JSON.stringify(log.extra) : undefined} mono />
          </tbody>
        </table>
      </Accordion>
    </div>
  );
}
